import { useRef } from "react";

/**
 * useSwipe — precise horizontal page-turn for touch.
 *
 * Only fires when a single finger travels a clear horizontal distance and the
 * gesture is meaningfully more horizontal than vertical, so scrolling the page
 * text (vertical) never accidentally turns a page.
 */
export function useSwipe(
  onSwipeLeft: () => void,
  onSwipeRight: () => void,
  { threshold = 55, ratio = 1.4 }: { threshold?: number; ratio?: number } = {}
) {
  const start = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      start.current = null; // ignore pinch / multi-touch
      return;
    }
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const s = start.current;
    start.current = null;
    if (!s) return;

    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;

    if (Math.abs(dx) < threshold) return; // too small to count
    if (Math.abs(dx) < Math.abs(dy) * ratio) return; // too vertical

    if (dx < 0) onSwipeLeft();
    else onSwipeRight();
  };

  return { onTouchStart, onTouchEnd };
}
